import { memo, useState } from 'react';
import { useRouter } from 'next/router';

import { FieldSet, Label, LabeledControl, Legend, Radio, Toast, useToast } from '@zillow/constellation-10';

import { DatarouterError } from '@zg-rentals/rental-manager-env';
import { useRentalManagerUserPropertyEditWebApiHandler_laundry } from '@zg-rentals/ts-rental-manager-api-client-react-query';

import { useGetListingDetails } from '../../../../hooks/queries/properties';
import { usePropertyEditMutation } from '../../../../hooks/usePropertyEditMutation';
import { getIsInputFormDisabled } from '../../utils';

import { KeyFeaturesConstants } from '../../../../constants/KeyFeaturesConstants';

import { useAuth } from '../../../AuthProvider';

const { RADIO_OPTIONS } = KeyFeaturesConstants;

const LaundryAmenities = (): JSX.Element | null => {
  const router = useRouter();
  const { user } = useAuth();
  const { enqueueToast } = useToast();

  const listingAlias = router.query.listingAlias as string;
  const { data: listingDetails } = useGetListingDetails(listingAlias);

  const laundry = listingDetails?.laundry || 'None';
  const canUpdateListing = user?.features?.canUpdateListing ?? false;

  const [localLaundry, setLocalLaundry] = useState(laundry);

  const isInputFormDisabled = getIsInputFormDisabled(listingDetails, canUpdateListing);

  const { mutateAsync: updateLaundry } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditWebApiHandler_laundry,
    successToastMessage: 'Laundry updated',
    meta: { context: { api: 'laundry', details: { listingAlias } } },
  });

  const save = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const fieldValue = event.target.value;
    const previousValue = localLaundry;
    const laundryType = RADIO_OPTIONS?.[fieldValue].key;

    setLocalLaundry(fieldValue);

    try {
      await updateLaundry({
        getParams: {
          laundry: laundryType,
          listingAlias,
        },
      });
    } catch (err) {
      setLocalLaundry(previousValue);
      const errorMessage = DatarouterError.getDisplayMessage(
        err,
        'Something went wrong, refresh the page and try again',
      );
      enqueueToast(<Toast tone="critical">{errorMessage}</Toast>);
    }
  };

  return (
    <FieldSet legend={<Legend>Laundry</Legend>} disabled={isInputFormDisabled}>
      {RADIO_OPTIONS &&
        Object.keys(RADIO_OPTIONS).map((fieldValue) => (
          <LabeledControl
            key={`laundryTypes-${RADIO_OPTIONS[fieldValue].label}`}
            label={<Label>{RADIO_OPTIONS[fieldValue].label}</Label>}
            control={
              <Radio
                name="LabeledControl-Laundry"
                value={fieldValue}
                checked={localLaundry === fieldValue}
                onChange={save}
              />
            }
          />
        ))}
    </FieldSet>
  );
};

export default memo(LaundryAmenities);
